import { useState, useEffect } from 'react';
import { get } from '../../../api/client';

interface StatsData {
  totalOrders: number;
  thisMonthOrders: number;
  lastMonthOrders: number;
  totalIncome: number;
  thisMonthIncome: number;
  lastMonthIncome: number;
  totalExpense: number;
  thisMonthExpense: number;
  lastMonthExpense: number;
  newCustomers: number;
  lastMonthCustomers: number;
  pendingReview: number;
  dailyIncome: number[];
  dailyExpense: number[];
}

function calcChange(current: number, last: number): { label: string; up: boolean } {
  if (last === 0 && current === 0) return { label: '暂无数据', up: true };
  if (last === 0) return { label: `本月新增 ${current}`, up: true };
  const pct = Math.round(((current - last) / last) * 100);
  if (pct === 0) return { label: '较上月持平', up: true };
  return {
    label: `较上月${pct > 0 ? '增长' : '减少'} ${Math.abs(pct)}%`,
    up: pct >= 0,
  };
}

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN', { minimumFractionDigits: 0 })}`;
}

function Sparkline({ data, color, gradientId }: { data: number[]; color: string; gradientId: string }) {
  if (!data.length || data.every(v => v === 0)) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 100;
  const h = 28;
  const pad = 2;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (w - pad * 2) + pad;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y] as [number, number];
  });
  const d = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
    .join(' ');
  const areaD = `${d} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;
  const lastPt = pts[pts.length - 1];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-7" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gradientId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="1.8" fill={color} />
    </svg>
  );
}

export default function StatsCards() {
  const [data, setData] = useState<StatsData>({
    totalOrders: 0, thisMonthOrders: 0, lastMonthOrders: 0,
    totalIncome: 0, thisMonthIncome: 0, lastMonthIncome: 0,
    totalExpense: 0, thisMonthExpense: 0, lastMonthExpense: 0,
    newCustomers: 0, lastMonthCustomers: 0, pendingReview: 0,
    dailyIncome: [], dailyExpense: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get<StatsData>('/stats').then(data => {
      if (data) {
        setData(data);
      }
      setLoading(false);
    }).catch(error => {
      console.error('获取统计数据失败:', error);
      setLoading(false);
    });
  }, []);

  const orderChange = calcChange(data.thisMonthOrders, data.lastMonthOrders);
  const incomeChange = calcChange(data.thisMonthIncome, data.lastMonthIncome);
  const expenseChange = calcChange(data.thisMonthExpense, data.lastMonthExpense);
  const customerChange = calcChange(data.newCustomers, data.lastMonthCustomers);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-5 md:mb-8">

      {/* 总订单 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 md:w-11 md:h-11 bg-teal-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-file-list-3-line text-base md:text-xl text-teal-600" />
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${orderChange.up ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
            {orderChange.up ? <i className="ri-arrow-up-line" /> : <i className="ri-arrow-down-line" />}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-1 truncate">总订单</p>
        <p className={`text-xl md:text-2xl font-bold text-gray-900 leading-none mb-1.5 ${loading ? 'animate-pulse' : ''}`}>
          {loading ? '-' : String(data.totalOrders)}
        </p>
        <p className="text-xs text-gray-400 leading-tight truncate">{loading ? '加载中...' : orderChange.label}</p>
      </div>

      {/* 总收入 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 md:w-11 md:h-11 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-money-dollar-circle-line text-base md:text-xl text-emerald-600" />
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${incomeChange.up ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
            {incomeChange.up ? <i className="ri-arrow-up-line" /> : <i className="ri-arrow-down-line" />}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-1 truncate">总收入</p>
        <p className={`text-xl md:text-2xl font-bold text-gray-900 leading-none mb-1 ${loading ? 'animate-pulse' : ''}`}>
          {loading ? '-' : formatAmount(data.totalIncome)}
        </p>
        <p className="text-xs text-gray-400 leading-tight truncate mb-2">{loading ? '加载中...' : incomeChange.label}</p>
        {!loading && <Sparkline data={data.dailyIncome} color="#10b981" gradientId="sparkIncomeGrad" />}
      </div>

      {/* 总支出 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 md:w-11 md:h-11 bg-rose-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-arrow-up-circle-line text-base md:text-xl text-rose-500" />
          </div>
          {/* 支出增加 = 不好，所以 up 为 true（增长）时用红色 */}
          <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${!expenseChange.up ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
            {!expenseChange.up ? <i className="ri-arrow-down-line" /> : <i className="ri-arrow-up-line" />}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-1 truncate">总支出</p>
        <p className={`text-xl md:text-2xl font-bold text-rose-600 leading-none mb-1 ${loading ? 'animate-pulse' : ''}`}>
          {loading ? '-' : formatAmount(data.totalExpense)}
        </p>
        <p className="text-xs text-gray-400 leading-tight truncate mb-2">{loading ? '加载中...' : expenseChange.label}</p>
        {!loading && <Sparkline data={data.dailyExpense} color="#f43f5e" gradientId="sparkExpenseGrad" />}
      </div>

      {/* 本月新客 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 md:w-11 md:h-11 bg-cyan-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-user-add-line text-base md:text-xl text-cyan-600" />
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${customerChange.up ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
            {customerChange.up ? <i className="ri-arrow-up-line" /> : <i className="ri-arrow-down-line" />}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-1 truncate">本月新客</p>
        <p className={`text-xl md:text-2xl font-bold text-gray-900 leading-none mb-1.5 ${loading ? 'animate-pulse' : ''}`}>
          {loading ? '-' : String(data.newCustomers)}
        </p>
        <p className="text-xs text-gray-400 leading-tight truncate">{loading ? '加载中...' : customerChange.label}</p>
      </div>

      {/* 待审核 */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="w-9 h-9 md:w-11 md:h-11 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-shield-line text-base md:text-xl text-amber-600" />
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${data.pendingReview > 0 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
            {data.pendingReview > 0 ? <i className="ri-alert-line" /> : <i className="ri-check-line" />}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-1 truncate">待审核</p>
        <p className={`text-xl md:text-2xl font-bold text-gray-900 leading-none mb-1.5 ${loading ? 'animate-pulse' : ''}`}>
          {loading ? '-' : String(data.pendingReview)}
        </p>
        <p className="text-xs text-gray-400 leading-tight truncate">
          {loading ? '加载中...' : data.pendingReview > 0 ? '需立即处理' : '暂无待审核'}
        </p>
      </div>

    </div>
  );
}
